import AnimateHeight from '@allesauto/core/animateHeight';
import Headline from '@allesauto/core/headline';
import NextImage from '@allesauto/core/nextImage';
import { FieldTypeAsset } from '@allesauto/utils/*';
import {
  CheckmarkOutline32,
  ChevronDown32,
  SubtractAlt32,
} from '@carbon/icons-react';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import { ReactNode, useState } from 'react';

enum Colors {
  BLACK = 'BLACK',
  GREY = 'GREY',
  PURPLE = 'PURPLE',
  WHITE = 'WHITE',
  GREEN = 'GREEN',
}

enum Position {
  IMAGE_LEFT = 'IMAGE_LEFT',
  IMAGE_RIGHT = 'IMAGE_RIGHT',
}

enum Icons {
  TICK = 'TICK',
  DASH = 'DASH',
}

interface ProsConsBox {
  _uid: string;
  headline: string;
  headlineTag: 3 | 4;
  headlineColor: keyof Omit<typeof Colors, Colors.GREY>;
  bulletPoints: {
    _uid: string;
    text: string;
    color: keyof Omit<typeof Colors, Colors.GREY>;
  }[];
  bulletPointsColor: Colors.BLACK | Colors.WHITE;
  background: keyof Omit<typeof Colors, Colors.BLACK>;
  icon: Icons;
}

interface ProsConsProps {
  _uid: string;
  component: string;
  headline: string;
  headlineTag: 2 | 3 | 4;
  headlineFontSize: 'title' | 'headline2' | 'headline3' | 'headline4';
  headlineColor: Colors.GREY | Colors.PURPLE | Colors.GREEN;
  text?: string;
  background: Colors.GREY | Colors.PURPLE | Colors.GREEN | Colors.WHITE;
  image: FieldTypeAsset;
  blocks: [ProsConsBox, ProsConsBox];
  position: Position;
}

interface AccordionProps {
  headline: string;
  headlineTag: 2 | 3 | 4;
  headlineColor: keyof Omit<typeof Colors, Colors.BLACK>;
  children: ReactNode;
  onClick: () => void;
  isOpen: boolean;
}

const cssClasses = {
  container: clsx(
    'px-15px py-30px md:px-30px md:py-60px lg:px-60px',
    'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
  ),
  checklistContainer: 'p-30px md:p-40px lg:p-[50px] ',
  headline: 'mb-20px',
  headlineContainer: 'lg:pr-[60px] md:col-span-3 mb-30px lg:mb-0 lg:col-span-1',
  checklistItem: 'flex gap-[16px] items-center mt-[24px]',
  checklistIcon: 'min-w-[24px] min-h-[24px]',
  imageVariantContainer: 'sm:grid-cols-1 md:grid-cols-1',
  imageVariantHeadlineContainer: clsx(
    'lg:mb-0 lg:pr-0',
    'flex flex-col justify-between sm:col-span-3 lg:pt-60px lg:relative',
    'lg:bg-[linear-gradient(90deg,_transparent_50%,_white_50%)]'
  ),
  order2: 'order-2',
  imageRightVariantHeadlineGreyContainer:
    'lg:bg-[linear-gradient(270deg,_transparent_50%,_white_50%)] lg:order-2',
  imageVariantImage:
    'sm:h-[150px] md:h-[200px] mb-[-75px] sm:mb-[-100px] relative w-full',
  desktopImageVariantImage: 'hidden lg:block b-0',
  imageVariantBlocksContainer: 'bg-white sm:pb-30px md:pb-40px pt-40px',
  accordionContainer: 'sm:pb-0 md:pb-0 col-span-3',
  accordion: 'border-0 flex items-center justify-between w-full',
  accordionIcon: 'ease-in-out text-grey80 text-headline2 transition',
  mobileOnly: 'block lg:hidden',
  imageVariantDesktopBlocks: 'hidden col-span-2 lg:grid grid-cols-2',
};

const themes = {
  [Colors.BLACK]: {
    background: 'bg-black monochrome:bg-black',
    text: 'text-black monochrome:text-black',
  },
  [Colors.PURPLE]: {
    background: 'bg-purple80 monochrome:bg-purple80',
    text: 'text-purple80 monochrome:text-purple80',
  },
  [Colors.WHITE]: {
    background: 'bg-white monochrome:bg-white',
    text: 'text-white monochrome:text-white',
  },
  [Colors.GREEN]: {
    background: 'bg-green60 monochrome:bg-green60',
    text: 'text-green60 monochrome:text-green60',
  },
  [Colors.GREY]: {
    background: 'bg-grey10 monochrome:bg-grey10',
    text: 'text-grey20 monochrome:text-grey20',
  },
};

const icons = {
  TICK: CheckmarkOutline32,
  DASH: SubtractAlt32,
};

const Accordion = (props: AccordionProps) => {
  const { headline, headlineTag, headlineColor, isOpen, children, onClick } =
    props;

  return (
    <div aria-expanded={isOpen}>
      <button onClick={onClick} type="button" className={cssClasses.accordion}>
        <Headline
          headlineTag={headlineTag}
          headlineText={headline}
          headlineColor={themes[headlineColor].text}
        />
        <ChevronDown32
          height={20}
          width={20}
          className={clsx(cssClasses.accordionIcon, isOpen && 'rotate-180')}
        />
      </button>
      <AnimateHeight show={isOpen}>{children}</AnimateHeight>
    </div>
  );
};

const ProsCons = (props: ProsConsProps) => {
  const {
    headline,
    headlineTag,
    headlineFontSize,
    headlineColor,
    background,
    image,
    blocks,
    position = Position.IMAGE_LEFT,
  } = props;
  const [openAccordion, setOpenAccordion] = useState(blocks[0]?._uid); // pros accordion open

  if (image?.filename) {
    const handleAccordionClick = (key: string) => {
      if (openAccordion === key) {
        setOpenAccordion('');
      } else {
        setOpenAccordion(key);
      }
    };

    return (
      <section
        className={clsx(
          cssClasses.container,
          cssClasses.imageVariantContainer,
          themes[background].background
        )}
        {...sbEditable(props)}
      >
        <div
          className={clsx(
            cssClasses.headlineContainer,
            cssClasses.imageVariantHeadlineContainer,
            position === Position.IMAGE_RIGHT &&
              cssClasses.imageRightVariantHeadlineGreyContainer
          )}
        >
          <Headline
            headlineTag={headlineTag}
            headlineText={headline}
            headlineColor={themes[headlineColor].text}
            headlineClassName={clsx(
              cssClasses.headline,
              headlineFontSize && `text-${headlineFontSize}`
            )}
          />
          <NextImage
            className={cssClasses.desktopImageVariantImage}
            imgClassName="!static"
            src={image.filename}
            alt={image.alt ?? ''}
            objectFit="contain"
            objectPosition="bottom"
            layout="fill"
          />
          <div
            className={clsx(
              cssClasses.imageVariantImage,
              cssClasses.mobileOnly
            )}
          >
            <NextImage
              className={clsx(cssClasses.mobileOnly)}
              src={image.filename}
              alt={image.alt ?? ''}
              layout="fill"
              objectFit="contain"
              imgClassName="!left-[50%] transform translate-x-[-50%]"
            />
          </div>
        </div>
        <div
          className={clsx(
            cssClasses.imageVariantBlocksContainer,
            cssClasses.mobileOnly
          )}
        >
          {blocks.map((block) => {
            const Icon = icons[block.icon];

            return (
              <div
                key={block._uid}
                className={clsx(
                  cssClasses.checklistContainer,
                  cssClasses.accordionContainer,
                  themes.WHITE.background
                )}
              >
                <Accordion
                  headline={block.headline}
                  headlineTag={block.headlineTag}
                  headlineColor={block.headlineColor}
                  isOpen={openAccordion === block._uid}
                  onClick={() => handleAccordionClick(block._uid)}
                >
                  {block.bulletPoints.map((bulletPoint) => (
                    <div
                      className={cssClasses.checklistItem}
                      key={bulletPoint._uid}
                    >
                      <Icon
                        height={24}
                        width={24}
                        className={clsx(
                          cssClasses.checklistIcon,
                          themes[block.headlineColor].text
                        )}
                      />
                      <p>{bulletPoint.text}</p>
                    </div>
                  ))}
                </Accordion>
              </div>
            );
          })}
        </div>
        <div className={cssClasses.imageVariantDesktopBlocks}>
          {blocks.map((block, index) => {
            const Icon = icons[block.icon];
            return (
              <div
                key={block._uid}
                className={clsx(
                  cssClasses.checklistContainer,
                  themes.WHITE.background,
                  position === Position.IMAGE_RIGHT &&
                    !index &&
                    cssClasses.order2
                )}
              >
                <Headline
                  headlineTag={block.headlineTag}
                  headlineText={block.headline}
                  headlineColor={themes[block.headlineColor].text}
                />
                {block.bulletPoints.map((bulletPoint) => (
                  <div
                    className={cssClasses.checklistItem}
                    key={bulletPoint._uid}
                  >
                    <Icon
                      height={24}
                      width={24}
                      className={clsx(
                        cssClasses.checklistIcon,
                        themes[block.headlineColor].text
                      )}
                    />
                    <p>{bulletPoint.text}</p>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  return (
    <section
      className={clsx(cssClasses.container, themes[background].background)}
      {...sbEditable(props)}
    >
      <div className={cssClasses.headlineContainer}>
        <Headline
          headlineTag={headlineTag}
          headlineText={headline}
          headlineColor={themes[headlineColor].text}
          headlineClassName={clsx(
            cssClasses.headline,
            headlineFontSize && `text-${headlineFontSize}`
          )}
        />
      </div>
      {blocks.map((block) => {
        const Icon = icons[block.icon];

        return (
          <div
            key={block._uid}
            className={clsx(
              cssClasses.checklistContainer,
              themes[block.bulletPointsColor].text,
              themes[block.background].background
            )}
          >
            <Headline
              headlineTag={block.headlineTag}
              headlineText={block.headline}
              headlineColor={themes[block.headlineColor].text}
            />
            {block.bulletPoints.map((bulletPoint) => (
              <div className={cssClasses.checklistItem} key={bulletPoint._uid}>
                <Icon
                  height={24}
                  width={24}
                  className={clsx(
                    cssClasses.checklistIcon,
                    themes[block.headlineColor].text
                  )}
                />
                <p>{bulletPoint.text}</p>
              </div>
            ))}
          </div>
        );
      })}
    </section>
  );
};

export default ProsCons;
